import GlossaryMarkdown from '~/widgets/GlossaryMarkdown/GlossaryMarkdown';
import type { GlossaryEntry } from '~/generated/glossary.types';
import styles from '~/widgets/GlossaryDefinition/GlossaryDefinition.module.scss';

type RecognitionMode = 'strict' | 'primary' | 'all';

interface GlossaryDefinitionProps {
  entry: GlossaryEntry;
  recognitionMode?: RecognitionMode;
  matchPlurality?: boolean;
}

/**
 * Inline glossary definition box that displays term/formula/rule definitions
 * within the documentation flow.
 */
export default function GlossaryDefinition({ entry, recognitionMode = 'strict', matchPlurality = false }: GlossaryDefinitionProps) {
  // Get the appropriate badge class based on type
  const getBadgeClass = () => {
    const type = entry.type.toLowerCase();
    if (type.includes('formula') || type.includes('derived')) {
      return styles.badgeFormula;
    }
    if (type.includes('rule')) {
      return styles.badgeRule;
    }
    return styles.badgeTerm;
  };

  return (
    <div
      className={styles.definition}
      data-type={entry.type}
      data-category={entry.category}
      role="region"
      aria-label={`Glossary entry: ${entry.name[0]} (${entry.type})`}
    >
      <div className={styles.header}>
        <span className={styles.title} role="heading" aria-level={3}>
          {entry.name[0]}
        </span>
        <span className={`${styles.badge} ${getBadgeClass()}`} aria-label={`Type: ${entry.type}`}>
          {entry.type}
        </span>
        <span className={`${styles.badge} ${styles.badgeCategory}`} aria-label={`Category: ${entry.category}`}>
          {entry.category}
        </span>
      </div>
      <GlossaryMarkdown className={styles.content} recognitionMode={recognitionMode} matchPlurality={matchPlurality}>
        {entry.content}
      </GlossaryMarkdown>
      {entry.expression && (
        <div className={styles.formula} aria-label={`Expression: ${entry.expression}`}>
          <code>{entry.expression}</code>
        </div>
      )}
    </div>
  );
}
